/**
 * Custom hooks for script data management using React Query
 */
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { scriptApi } from "../services/api";
import {
  Script,
  ScriptCreate,
  ScriptUpdate,
  ScriptFilters,
  VersionControlAction,
  ScriptDuplicateRequest,
  ScriptApproveRequest,
  ScriptRejectRequest,
} from "../types/script";

/**
 * Hook for fetching paginated scripts list
 */
export const useScripts = (filters: ScriptFilters = {}) => {
  return useQuery(["scripts", filters], () => scriptApi.getScripts(filters), {
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching a single script by ID
 */
export const useScript = (id: number) => {
  return useQuery(["script", id], () => scriptApi.getScript(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for creating a new script
 */
export const useCreateScript = () => {
  const queryClient = useQueryClient();

  return useMutation((data: ScriptCreate) => scriptApi.createScript(data), {
    onSuccess: (newScript) => {
      // Invalidate scripts list to refresh data
      queryClient.invalidateQueries(["scripts"]);
      toast.success(`Script "${newScript.name}" created successfully`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || "Failed to create script";
      toast.error(message);
    },
  });
};

/**
 * Hook for updating an existing script
 */
export const useUpdateScript = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }: { id: number; data: ScriptUpdate }) =>
      scriptApi.updateScript(id, data),
    {
      onSuccess: (updatedScript) => {
        // Update the cache for this specific script
        queryClient.setQueryData(["script", updatedScript.id], updatedScript);
        // Invalidate scripts list to refresh data
        queryClient.invalidateQueries(["scripts"]);
        toast.success(`Script "${updatedScript.name}" updated successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to update script";
        toast.error(message);
      },
    },
  );
};

/**
 * Hook for deleting a script
 */
export const useDeleteScript = () => {
  const queryClient = useQueryClient();

  return useMutation((id: number) => scriptApi.deleteScript(id), {
    onSuccess: (_, deletedId) => {
      // Remove the script from cache
      queryClient.removeQueries(["script", deletedId]);
      // Invalidate scripts list to refresh data
      queryClient.invalidateQueries(["scripts"]);
      toast.success("Script deleted successfully");
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || "Failed to delete script";
      toast.error(message);
    },
  });
};

/**
 * Hook for script version control operations
 */
export const useScriptVersionControl = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, action }: { id: number; action: VersionControlAction }) =>
      scriptApi.versionControl(id, action),
    {
      onSuccess: (updatedScript, variables) => {
        const { action } = variables;

        // Update the cache for this specific script
        queryClient.setQueryData(["script", updatedScript.id], updatedScript);
        // Invalidate scripts list to refresh data
        queryClient.invalidateQueries(["scripts"]);

        // Show appropriate success message
        switch (action.action) {
          case "publish":
            toast.success(
              `Script "${updatedScript.name}" published successfully`,
            );
            break;
          case "unpublish":
            toast.success(
              `Script "${updatedScript.name}" unpublished successfully`,
            );
            break;
          case "create_version":
            toast.success(
              `New version of "${updatedScript.name}" created successfully`,
            );
            break;
        }
      },
      onError: (error: any, variables) => {
        const { action } = variables;
        const defaultMessage = `Failed to ${action.action} script`;
        const message = error.response?.data?.detail || defaultMessage;
        toast.error(message);
      },
    },
  );
};

export const useDuplicateScript = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: ScriptDuplicateRequest }) =>
      scriptApi.duplicate(id, data),
    {
      onSuccess: (draft) => {
        queryClient.invalidateQueries(["scripts"]);
        toast.success(`Draft created for "${draft.name}"`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to duplicate script";
        toast.error(message);
      },
    },
  );
};

export const useApproveScript = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: ScriptApproveRequest }) =>
      scriptApi.approve(id, data),
    {
      onSuccess: (published) => {
        queryClient.invalidateQueries(["scripts"]);
        queryClient.setQueryData(["script", published.id], published);
        toast.success(`Published "${published.name}"`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to approve script";
        toast.error(message);
      },
    },
  );
};

export const useRejectScript = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: ScriptRejectRequest }) =>
      scriptApi.reject(id, data),
    {
      onSuccess: (archived) => {
        queryClient.invalidateQueries(["scripts"]);
        queryClient.setQueryData(["script", archived.id], archived);
        toast.success(`Draft archived`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to reject script";
        toast.error(message);
      },
    },
  );
};
